import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { type DocumentType } from '../constants/documentTypes';

export interface DocumentDownloadResponse {
    status: 'success' | 'error';
    details?: {
        extension: string;
        fileContent: string; // Base64 encoded
    };
    message?: string;
}

/**
 * Download a document from the server
 * 
 * For document type to parentId mapping, see: @/modules/profile/constants/documentTypes.ts
 * 
 * @param docType - Document type (e.g., SALES_SOA, TITLE_DEED, etc.)
 * @param customerId - Customer ID (required)
 * @param parentId - Optional parent ID. The type of ID depends on docType.
 *                   See documentTypes.ts for full documentation on which ID type to use for each document type.
 */
export async function downloadDocument(
    docType: DocumentType,
    customerId: string,
    parentId?: string
): Promise<DocumentDownloadResponse> {
    try {
        logger.info('DocumentDownloadService', 'Downloading document', {
            docType,
            customerId,
            parentId,
        });

        const isAdhaJourney = useAppStore.getState().isAdhaJourney;
        const params: Record<string, string> = {
            docType,
        };

        if (parentId) {
            params.parentId = parentId;
        }

        if (!isAdhaJourney) {
            params.customerId = customerId;
        }

        // apiClient.get returns Promise<T> directly (already unwrapped from AxiosResponse)
        // The API returns: { status: "success", details: { extension: "PNG", fileContent: "..." } }
        const responseData = await apiClient.get<DocumentDownloadResponse>(
            Endpoints.profile.documentDownload,
            {
                params,
            }
        );

        // Log the response structure for debugging
        logger.debug('DocumentDownloadService', 'API response received', {
            docType,
            responseType: typeof responseData,
            responseKeys: responseData ? Object.keys(responseData) : [],
            status: responseData?.status,
            hasDetails: !!responseData?.details,
            hasFileContent: !!responseData?.details?.fileContent,
            extension: responseData?.details?.extension,
            fileContentLength: responseData?.details?.fileContent?.length || 0,
        });

        // Validate response structure
        if (!responseData) {
            logger.error('DocumentDownloadService', 'Invalid response: missing data', {
                docType,
            });
            return {
                status: 'error',
                message: 'Invalid response: missing data',
            };
        }

        if (responseData.status !== 'success') {
            logger.warn('DocumentDownloadService', 'API returned error status', {
                docType,
                status: responseData.status,
                message: responseData.message,
            });
            return responseData; // Return the error response
        }

        if (!responseData.details?.fileContent) {
            logger.warn('DocumentDownloadService', 'Missing file content in response', {
                docType,
                status: responseData.status,
                hasDetails: !!responseData.details,
                detailsKeys: responseData.details ? Object.keys(responseData.details) : [],
            });
            return {
                status: 'error',
                message: 'No file content in response',
            };
        }

        logger.info('DocumentDownloadService', 'Document downloaded successfully', {
            docType,
            hasContent: !!responseData.details?.fileContent,
            extension: responseData.details?.extension,
            contentLength: responseData.details.fileContent.length,
        });

        return responseData;
    } catch (error: any) {
        // Better error logging
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        const errorStatus = error?.response?.status;
        const errorData = error?.response?.data;
        
        logger.warn('DocumentDownloadService', 'Failed to download document', {
            docType,
            customerId,
            errorStatus,
            errorMessage,
            errorData,
        });
        
        // Return error response instead of throwing
        // 400/404 typically means document doesn't exist - this is expected for some users
        return {
            status: 'error',
            message: errorStatus === 400 || errorStatus === 404 
                ? 'Document not available' 
                : errorMessage,
        };
    }
}

